import { useZero } from "@rocicorp/zero/react";
import { styled } from "goober";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { flushSync } from "react-dom";
import { getTargetValue } from "../../../shared/connectors/getTargetValue";
import {
  applyNormalize,
  parseUrlInput,
  computeSegments,
  type Segment,
} from "../../../shared/connectors/normalizeInput";
import type {
  HostnameField,
  InputField,
  SelectField,
  UrlField,
} from "../../../shared/connectors/types";
import { sharedMutators } from "../../../shared/mutators";
import {
  queueConnectorEdit,
  withConnectorDestinationProof,
} from "../../sealSecret";
import { sealSecretInput } from "./sealSecretInput";
import { Box } from "../Box";
import { Dropdown } from "../Dropdown";
import { Icon } from "../Icon";
import { Input } from "../Input";
import { DropdownOpener } from "../Settings/shared";
import { Text } from "../Text";
import type { App } from "./Form";
import { useConfirmGrantRemoval } from "./useConfirmGrantRemoval";
import { GeneratePrivateKeyButton } from "./GeneratePrivateKeyButton";
import { useRenderWithPlaceholders } from "./renderWithPlaceholders";

const InputContainer = styled("div")`
  display: flex;
  flex-direction: column;
`;

const NormalizedInputWrapper = styled("div")`
  position: relative;
`;

const SegmentOverlay = styled("div")`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
  padding: 8px 12px;
  font-size: 12px;
  font-family: InterVariable;
  line-height: 16px;
  white-space: pre;
  overflow: hidden;
`;

type Props = {
  item: InputField | UrlField | HostnameField | SelectField;
  app: App;
  locallySet?: ReadonlySet<string>;
  onFieldSet?: (key: string) => void;
  disabled?: boolean;
  onEdit?: () => void;
};

const normalizeForKind = (kind: Props["item"]["kind"]) => {
  if (kind === "baseUrl") return (url: URL) => url;
  if (kind === "fullUrl") return (url: URL) => url.href;
  if (kind === "hostname") return (url: URL) => url.hostname;
  return undefined;
};

export function FieldItem({
  item,
  app,
  locallySet,
  onFieldSet,
  disabled,
  onEdit,
}: Props) {
  const z = useZero();
  const { removesGrants, confirmGrantRemoval, confirmModal } =
    useConfirmGrantRemoval(app);
  const renderWithPlaceholders = useRenderWithPlaceholders();
  const value = getTargetValue(item.target, app);
  const { target } = item;
  const fieldKey = `${target.kind}:${target.field}`;
  const isMasked = target.kind === "secrets" || target.field === "clientSecret";
  // Secret values never replicate to the client; presence comes from the
  // non-secret `secretKeys`/`hasOAuthClientSecret` companions the server maintains,
  // plus this session's own edits before they round-trip.
  const secretIsSet =
    (locallySet?.has(fieldKey) ?? false) ||
    (target.kind === "secrets"
      ? (app.secretKeys?.includes(target.field) ?? false)
      : target.field === "clientSecret"
        ? (app.hasOAuthClientSecret ?? false)
        : false);
  const selectedValue = value || item.defaultValue || "";
  const selectedLabel =
    item.kind === "select"
      ? item.options.find((o) => o.value === selectedValue)?.label
      : undefined;
  const normalizeFn = normalizeForKind(item.kind);
  const [segments, setSegments] = useState<Segment[] | null>(null);
  const [generatedKey, setGeneratedKey] = useState<{
    value: string;
    publicKey: string;
    revision: number;
  }>();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const appRef = useRef(app);
  useLayoutEffect(() => {
    appRef.current = app;
  });
  // The input is uncontrolled (defaultValue) so a sibling can edit the same
  // field — e.g. the workflow picker renaming the app — without us losing
  // typed-in changes. Sync from `value` only when the input isn't focused.
  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    if (document.activeElement === el) return;
    if (el.value !== (value ?? "")) {
      el.value = value ?? "";
    }
  }, [value]);

  const saveValue = async (rawValue: string, publicKey?: string) => {
    if (normalizeFn && rawValue.trim() && !parseUrlInput(rawValue)) return;
    const newValue = normalizeFn
      ? applyNormalize(rawValue, normalizeFn)
      : rawValue;
    if ((isMasked || item.required) && !newValue) {
      inputRef.current?.setCustomValidity("");
      return;
    }
    let secretSeal: { field: string; value: string } | undefined;
    if (target.kind === "secrets") {
      const input = inputRef.current;
      if (!input) return;
      const value = await sealSecretInput(input, newValue, {
        app: appRef.current,
        field: target.field,
      });
      if (value === undefined) return;
      secretSeal = { field: target.field, value };
    }
    if (isMasked && newValue && target.kind !== "secrets")
      onFieldSet?.(fieldKey);
    const resetInput = () => {
      if (inputRef.current) inputRef.current.value = value ?? "";
      setSegments(null);
    };
    const saveOrConfirmOnBlur = <T,>(
      update: Parameters<typeof removesGrants>[0],
      save: () => T
    ): T | undefined => {
      if (document.activeElement === inputRef.current && removesGrants(update))
        return;
      return confirmGrantRemoval(update, save, resetInput);
    };
    if (target.kind === "oauthConnection") {
      const { oauthConnectionId } = app;
      if (!oauthConnectionId) return;
      const scopes = newValue.split(/\s+/).filter((s) => s.length > 0);
      return saveOrConfirmOnBlur(
        target.field === "clientId"
          ? { oauthClientId: newValue }
          : target.field === "scopes"
            ? { oauthRequestedScopes: scopes }
            : {},
        () =>
          z.mutate(
            sharedMutators.updateOAuthConnection({
              oauthConnectionId,
              [target.field]: target.field === "scopes" ? scopes : newValue,
            })
          ).server
      );
    }

    if (target.kind === "connectedApp") {
      const update = withConnectorDestinationProof(appRef.current, {
        [target.field]: newValue,
      });
      if (update instanceof Promise)
        return update.then(
          (args) => z.mutate(sharedMutators.updateConnectedApp(args)).server
        );
      return z.mutate(sharedMutators.updateConnectedApp(update)).server;
    }

    if (secretSeal) {
      const generation = item.kind === "input" ? item.generate : undefined;
      inputRef.current?.setCustomValidity(
        "Please wait for the secret to finish saving."
      );
      const result = await z.mutate(
        sharedMutators.updateConnectedApp(
          await withConnectorDestinationProof(appRef.current, {
            ...(generation
              ? {
                  configurationPatch: {
                    [generation.publicKeyField]:
                      publicKey ??
                      (newValue === generatedKey?.value
                        ? generatedKey.publicKey
                        : ""),
                  },
                }
              : {}),
            secrets: { [secretSeal.field]: secretSeal.value },
          })
        )
      ).server;
      inputRef.current?.setCustomValidity(
        result.type === "success" ? "" : "The secret wasn’t saved. Try again."
      );
      if (result.type === "success") onFieldSet?.(fieldKey);
      return result;
    }

    const updated = {
      ...appRef.current.configuration,
      [target.field]: newValue,
    };
    const update = withConnectorDestinationProof(appRef.current, {
      configurationPatch: { [target.field]: newValue },
    });
    const save = (
      args: Parameters<typeof sharedMutators.updateConnectedApp>[0]
    ) =>
      saveOrConfirmOnBlur(
        { configuration: updated },
        () => z.mutate(sharedMutators.updateConnectedApp(args)).server
      );
    if (update instanceof Promise) return update.then(save);
    return save(update);
  };

  const setValue = (rawValue: string, publicKey?: string) =>
    queueConnectorEdit(app.tenantId, app.id, async () => {
      try {
        const result = await saveValue(rawValue, publicKey);
        if (result?.type === "error")
          inputRef.current?.setCustomValidity(
            "This change wasn’t saved. Try again."
          );
        else if (result?.type === "success")
          inputRef.current?.setCustomValidity("");
      } catch {
        inputRef.current?.setCustomValidity(
          "This change wasn’t saved. Try again."
        );
      }
    });

  return (
    <div>
      <Box horizontal={8} vertical={10}>
        <Text variant="text-small-md" color="var(--text-primary)">
          {item.label}
        </Text>
      </Box>
      <InputContainer>
        {item.kind === "select" ? (
          <Dropdown
            opener={
              <DropdownOpener type="button" disabled={disabled}>
                <Text
                  variant="text-regular"
                  color={
                    selectedLabel === undefined
                      ? "var(--text-secondary)"
                      : "var(--text-primary)"
                  }
                >
                  {selectedLabel ?? "Select…"}
                </Text>
                <span style={{ opacity: 0.3 }}>
                  <Icon name="chevron-down" size={12} />
                </span>
              </DropdownOpener>
            }
            align="bottom-left"
            sections={[
              {
                items: item.options.map((option) => ({
                  type: "button" as const,
                  left: null,
                  label: option.label,
                  right:
                    option.value === selectedValue ? (
                      <Icon
                        name="check"
                        size={12}
                        color="var(--text-secondary)"
                      />
                    ) : undefined,
                  onClick: () => {
                    onEdit?.();
                    setValue(option.value);
                  },
                })),
              },
            ]}
          />
        ) : (
          <NormalizedInputWrapper>
            {segments && (
              <SegmentOverlay>
                {segments.map((seg) => (
                  <span
                    key={`${seg.kept}-${seg.text}`}
                    style={{ opacity: seg.kept ? 1 : 0.3 }}
                  >
                    {seg.text}
                  </span>
                ))}
              </SegmentOverlay>
            )}
            <Input
              key={generatedKey?.revision}
              ref={inputRef}
              type="text"
              // A set secret shows as placeholder dots, not a value, so don't
              // let `required` flag a set masked field as empty.
              required={item.required && !(isMasked && secretIsSet)}
              secret={isMasked}
              secretIsSet={secretIsSet}
              defaultValue={
                generatedKey?.value ||
                value ||
                (item.defaultValue
                  ? renderWithPlaceholders(item.defaultValue)
                  : undefined)
              }
              placeholder={item.placeholder}
              style={{
                width: "100%",
                ...(segments
                  ? { color: "transparent", caretColor: "var(--text-primary)" }
                  : {}),
              }}
              onChange={(e) => {
                if (normalizeFn) {
                  const raw = e.target.value;
                  e.target.setCustomValidity(
                    !raw.trim() || parseUrlInput(raw) ? "" : "Enter a valid URL"
                  );
                  const normalized = applyNormalize(raw, normalizeFn);
                  if (normalized !== raw) {
                    setSegments(computeSegments(raw, normalizeFn));
                  } else {
                    setSegments(null);
                  }
                }
              }}
              onBlur={(e) => {
                if (item.required && !e.target.value && value) {
                  e.target.value = value;
                }
              }}
              onFinishChange={setValue}
              readOnly={disabled}
            />
          </NormalizedInputWrapper>
        )}
      </InputContainer>
      {item.helperText && (
        <Box horizontal={8} top={6}>
          <Text variant="text-small" color="var(--text-secondary)">
            {renderWithPlaceholders(item.helperText)}
          </Text>
        </Box>
      )}
      {item.kind === "input" && item.generate?.type === "p256-private-key" && (
        <div
          style={{ display: "flex", justifyContent: "flex-end", marginTop: 12 }}
        >
          <GeneratePrivateKeyButton
            disabled={disabled}
            onGenerate={async (privateKey, publicKey) => {
              onEdit?.();
              flushSync(() => {
                setGeneratedKey((previous) => ({
                  value: privateKey,
                  publicKey,
                  revision: (previous?.revision ?? 0) + 1,
                }));
              });
              await setValue(privateKey, publicKey);
            }}
          />
        </div>
      )}
      {confirmModal}
    </div>
  );
}
